import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';

const PAGE_SIZE = 50;

export interface Assessment {
  id: number;
  assessment_id: string;
  title: string;
  status: string;
  created_at: string;
  updated_at: string;
  progress_bar: {
    role_title: string;
    job_description: string;
    criteria: string;
    technical_competence: string;
    time_line: string | null;
    done: string;
  };
  // Add any other assessment fields from your assessment response
}

export interface ConversationSession {
  id: number;
  title?: string;
  assessment: Assessment | null;
  current_state: string;
  created_at: string;
  updated_at: string;
  flow: number;
}

export interface ConversationSessionsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ConversationSession[];
}

/**
 * Hook for fetching conversation sessions with infinite scroll pagination (50 items per page)
 */
export const useConversationSessions = () => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['conversationSessions'],
    queryFn: async ({ pageParam = 1 }) => {
      const response = await apiClient.get<ConversationSessionsResponse>(
        '/assessment/assistant-conversation/',
        { params: { page: pageParam, page_size: PAGE_SIZE } },
      );
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      // If there's a next URL, return the next page number
      if (lastPage.next) {
        return allPages.length + 1;
      }
      return undefined;
    },
  });

  // Flatten all pages into a single ConversationSessionsResponse shape
  // so consumers don't need to change how they read the data
  const conversationSessions: ConversationSessionsResponse | undefined = data
    ? {
        count: data.pages[0]?.count ?? 0,
        next: data.pages[data.pages.length - 1]?.next ?? null,
        previous: data.pages[0]?.previous ?? null,
        results: data.pages.flatMap(page => page.results),
      }
    : undefined;

  const searchMutation = useMutation({
    mutationFn: async (searchTerm: string) => {
      const response = await apiClient.get<ConversationSessionsResponse>('/assessment/assistant-conversation/', {
        params: { search: searchTerm, page_size: PAGE_SIZE },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to search conversations';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    conversationSessions,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchConversationSessions: () => queryClient.invalidateQueries({ queryKey: ['conversationSessions'] }),
    searchConversations: searchMutation.mutate,
    isSearching: searchMutation.isPending,
  };
};

/**
 * Hook for fetching a specific conversation session by assistant ID
 */
export const useConversationSessionDetail = (assistantId: number | string | null) => {
  const { openToast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['conversationSessionDetail', assistantId],
    queryFn: async () => {
      const response = await apiClient.get<ConversationSession>(`/assessment/assistant-conversation/${assistantId}/`);
      return response.data;
    },
    enabled: !!assistantId,
    retry: 1,
  });

  return {
    conversationSession: data,
    isLoading,
    error,
    refetch,
    // Helper to determine if this is flow2 (conversation has progressed past CV-analysis).
    // Combines three signals because none alone is reliable:
    //   - data.flow === 2 (only set when entry point was JD generation, not CV analysis)
    //   - assessment.status !== 'draft' (set once the assessment goes live; flow_1 keeps it at 'draft')
    //   - progress_bar.criteria is set (during the criteria-building stage; can be null once published)
    isFlow2:
      data?.flow === 2 ||
      (!!data?.assessment && data?.assessment?.status !== 'draft') ||
      !!data?.assessment?.progress_bar?.criteria,
    // Get the assessment ID if in flow2
    assessmentId: data?.assessment?.assessment_id || null,
  };
};